import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { apiUrls } from "../helpers/apiUrls";
import { useAxiosPrivate } from "../hooks/useAxiosPrivate";
import { statusApiSchema } from "../schemas/status";
import { datetimeWithOffset, paginatedResponse } from "../utils";
import { CONTACTS_QUERY_KEY, contactsApiSchema } from "./contactsService";
import { customersApiSchema } from "./customersService";
import { productsApiSchema } from "./productsService";
import { usersApiSchema } from "./usersApi";

const TaskSubjectSchema = z.object({
    id: z.string(),
    name: z.string(),
    name_eng: z.string(),
    ordering: z.number(),
});

const TaskSubsubjectSchema = z.object({
    id: z.string(),
    subject: TaskSubjectSchema,
    name: z.string(),
    name_eng: z.string(),
});

const TaskSchema = z.object({
    id: z.string(),
    name: z.string(),
    way: z.enum(["PHONE", "EMAIL", "MEETING"]),
    start: datetimeWithOffset.nullable(),
    stop: datetimeWithOffset.nullable(),
    done: datetimeWithOffset.nullable(),
    priority: z.enum(["LOW", "NORMAL", "HIGH"]),
    note: z.string(),
    subject: TaskSubjectSchema,
    subsubject: TaskSubsubjectSchema.nullable(),
    customer: customersApiSchema.customer.nullable(),
    seller: usersApiSchema.seller,
    products: z.array(productsApiSchema.product),
    contacts: z.array(contactsApiSchema.contact),
    status: statusApiSchema.status,
    is_allday: z.boolean(),
    created: datetimeWithOffset,
});

const TaskUpdateSchema = z.object({
    name: z.string().optional(),
    way: z.enum(["PHONE", "EMAIL", "MEETING"]).optional(),
    start: z.string().datetime().optional().nullable(),
    stop: z.string().datetime().optional().nullable(),
    done: z.string().datetime().optional(),
    priority: z.enum(["LOW", "NORMAL", "HIGH"]).optional(),
    note: z.string().optional(),
    subject: z.string().optional(),
    customer: z.string().optional(),
    seller: z.string().optional(),
    products: z.array(z.string()).optional(),
    contacts: z.array(z.string()).optional(),
    status: z.string().optional(),
    is_allday: z.boolean().optional(),
});

const TaskStatusSchema = z.object({
    id: z.string().optional(),
    status_name: z.string(),
    status_name_eng: z.string(),
    status_key: z.string(),
});

export const tasksApiSchema = {
    getTaskSubjectsResponse: paginatedResponse(TaskSubjectSchema),
    getTaskSubsubjectsResponse: z.array(TaskSubsubjectSchema),
    getTaskStatusesResponse: paginatedResponse(TaskStatusSchema),
    getTasksResponse: z.array(TaskSchema),
    task: TaskSchema,
    taskSubject: TaskSubjectSchema,
};

export const tasksApi = {
    getTasks: (axios, params) => {
        return axios
            .get(apiUrls.tasks.list, {
                params,
            })
            .then((response) => response.data);
    },

    async getTasksForUser(axios, params, userId, taskSubjectNameEng, taskWay) {
        params.set("seller_user_id", userId);
        if (taskSubjectNameEng && taskSubjectNameEng !== "all") {
            params.set("subject", taskSubjectNameEng);
        }
        if (taskWay && taskWay?.toLowerCase() !== "all") {
            params.set("way", taskWay);
        }

        const response = await axios.get(apiUrls.tasks.list, { params });

        return response.data; // TODO: Make sure parsing gives an error
    },

    async getTaskSubjects(axios) {
        const response = await axios.get(apiUrls.tasks.subjects);
        return tasksApiSchema.getTaskSubjectsResponse.parse(response.data);
    },

    async getTaskSubsubjects(axios, subjectId) {
        const params = new URLSearchParams();
        if (subjectId) {
            params.set("subject_id", subjectId);
        }
        const response = await axios.get(apiUrls.tasks.subSubjects, { params });
        return response.data; // TODO: Make sure parsing gives an error
    },

    async getTaskStatuses(axios) {
        const response = await axios.get(apiUrls.tasks.statuses);
        return response.data; // TODO: Make sure parsing gives an error
    },

    async createTask(axios, payload) {
        return await axios.post(apiUrls.tasks.url, payload);
    },

    async updateTask(axios, task, payload) {
        const response = await axios.patch(apiUrls.tasks.task(task.id), payload);
        if (response.status !== 200) {
            // TODO: handle this somehow
            throw new Error("Failed to update task");
        }
        return response.data; // TODO: Make sure parsing gives an error
    },
    async getTasksForCustomer(axios, params, customerId) {
        params.set("customer_id", customerId);
        const response = await axios.get(apiUrls.tasks.list, { params });
        return response.data;
    },

    async finalizeTask(axios, taskId, payload) {
        const response = await axios.post(apiUrls.tasks.finalize(taskId), payload);
        if (response.status !== 200) {
            throw new Error("Failed to finalize task");
        }
        return response.data; // TODO: Make sure parsing gives an error
    },
};

export function useGetTasks(params = new URLSearchParams(), options = {}) {
    const axiosPriv = useAxiosPrivate();
    return useQuery({
        ...options,
        queryKey: ["task", Object.fromEntries(params.entries())],
        queryFn: () => {
            return tasksApi.getTasks(axiosPriv, params);
        },
    });
}

export function useGetTasksForUser(
    userId,
    taskSubjectNameEng,
    taskWay,
    searchParams = new URLSearchParams(),
    options = {},
) {
    const axiosPriv = useAxiosPrivate();

    return useQuery({
        ...options,
        // enabled: !!userId && options.enabled,
        queryKey: ["tasksFor", userId, taskWay, taskSubjectNameEng],
        queryFn: () => {
            return tasksApi.getTasksForUser(
                axiosPriv,
                searchParams,
                userId,
                taskSubjectNameEng,
                taskWay,
            );
        },
    });
}

export function useGetTasksForUserWithDateRange(
    userId,
    taskSubjectNameEng,
    taskWay,
    startDate,
    endDate,
    options = {},
) {
    const axiosPriv = useAxiosPrivate();
    const queryKey = [
        "tasksForDateRange",
        userId,
        taskWay,
        taskSubjectNameEng,
        startDate?.toISOString(),
        endDate?.toISOString(),
    ];

    return useQuery({
        ...options,
        enabled: !!userId && !!startDate && !!endDate && options.enabled !== false,
        queryKey,
        queryFn: () => {
            if (!startDate || !endDate) {
                return Promise.resolve([]);
            }

            const params = new URLSearchParams();
            params.set("seller_user_id", userId);

            if (taskSubjectNameEng && taskSubjectNameEng !== "all") {
                params.set("subject", taskSubjectNameEng);
            }
            if (taskWay && taskWay?.toLowerCase() !== "all") {
                params.set("way", taskWay);
            }

            params.set("start_after", startDate.toISOString());
            params.set("start_before", endDate.toISOString());

            return tasksApi.getTasks(axiosPriv, params);
        },
    });
}

export function useGetTasksForCustomer(customerId, options = {}) {
    const axiosPriv = useAxiosPrivate();

    return useQuery({
        ...options,
        enabled: !!customerId && options.enabled !== false,
        queryKey: ["tasksForCustomer", customerId],
        queryFn: () => {
            return tasksApi.getTasksForCustomer(axiosPriv, new URLSearchParams(), customerId);
        },
    });
}

export function useGetTaskSubjects(options = {}) {
    const axiosPriv = useAxiosPrivate();
    return useQuery({
        ...options,
        queryKey: ["taskSubjects"],
        queryFn: () => {
            return tasksApi.getTaskSubjects(axiosPriv);
        },
    });
}

export function useUpdateTask(options = {}) {
    const axiosPriv = useAxiosPrivate();
    const queryClient = useQueryClient();

    return useMutation({
        ...options,
        mutationKey: ["updateTask"],
        mutationFn: ({ task, payload }) => {
            return tasksApi.updateTask(axiosPriv, task, payload);
        },
        onMutate: async ({ task, payload }) => {
            await queryClient.cancelQueries({ queryKey: getQueryKeyForTask(task) });

            const previousTasks = queryClient.getQueryData(getQueryKeyForTask(task));

            // TODO: refactor these invalidates
            queryClient.setQueryData(getQueryKeyForTask(task), (oldData) =>
                optimisticUpdateTasks(oldData, task, payload),
            );
            queryClient.setQueryData(["tasksFor", task.seller.user_id, null, null], (oldData) =>
                optimisticUpdateTasks(oldData, task, payload),
            );

            return { previousTasks };
        },
        onSuccess: (data, variables, context) => {
            options.onSuccess?.(data, variables, context);
        },
        onError: (error, variables, context) => {
            if (context?.previousTasks) {
                queryClient.setQueryData(getQueryKeyForTask(variables.task), context.previousTasks);
            }
            options.onError?.(error, variables, context);
        },
        onSettled: (_data, _error, variables, _context) => {
            queryClient.invalidateQueries({ queryKey: ["tasksFor"] });
            queryClient.invalidateQueries({ queryKey: ["tasksForDateRange"] });
        },
    });
}

export function useGetTaskSubsubjects(subjectId, options = {}) {
    const axiosPriv = useAxiosPrivate();

    return useQuery({
        ...options,
        queryKey: ["taskSubsubjects", subjectId],
        queryFn: () => {
            return tasksApi.getTaskSubsubjects(axiosPriv, subjectId);
        },
        enabled: options.enabled !== false,
    });
}

export function useGetTaskStatuses(options = {}) {
    const axiosPriv = useAxiosPrivate();

    return useQuery({
        ...options,
        queryKey: ["taskStatuses"],
        queryFn: () => {
            return tasksApi.getTaskStatuses(axiosPriv);
        },
    });
}

export function useCreateTaskMutation(options = {}) {
    const axiosPriv = useAxiosPrivate();
    const queryClient = useQueryClient();

    return useMutation({
        ...options,
        mutationFn: (payload) => {
            return tasksApi.createTask(axiosPriv, payload);
        },
        onSuccess: (data, variables, context) => {
            // Invalidate relevant queries to refresh task lists
            queryClient.invalidateQueries({ queryKey: ["tasksFor"] });
            queryClient.invalidateQueries({ queryKey: ["tasksForDateRange"] });
            queryClient.invalidateQueries({ queryKey: ["tasksForCustomer"] });

            options.onSuccess?.(data, variables, context);
        },
    });
}

export function useFinalizeTaskMutation(options = {}) {
    const axiosPriv = useAxiosPrivate();
    const queryClient = useQueryClient();

    return useMutation({
        ...options,
        mutationFn: ({ taskId, ...payload }) => {
            return tasksApi.finalizeTask(axiosPriv, taskId, payload);
        },
        onSuccess: (data, variables, context) => {
            // Invalidate relevant queries to refresh task lists
            queryClient.invalidateQueries({ queryKey: ["tasksFor"] });
            queryClient.invalidateQueries({ queryKey: ["tasksForDateRange"] });
            queryClient.invalidateQueries({ queryKey: ["tasksForCustomer"] });
            queryClient.invalidateQueries({ queryKey: [CONTACTS_QUERY_KEY] });

            options.onSuccess?.(data, variables, context);
        },
    });
}

function optimisticUpdateTasks(oldData, task, payload) {
    if (!oldData) {
        return oldData;
    }

    return oldData.map((oldTask) =>
        oldTask.id === task.id
            ? {
                  ...oldTask,
                  start: payload.start ?? null,
                  stop: payload.stop ?? null,
                  isAllDay: payload.is_allday ?? oldTask.is_allday,
              }
            : oldTask,
    );
}

function getQueryKeyForTask(task) {
    return ["tasksFor", task.seller.user_id, task.way, task.subject.name_eng];
}
